import React, {useRef} from "react";
import {Helmet} from "react-helmet-async";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import HeroSection from "@/components/homepage/HeroSection";
import FeaturedProcesses from "@/components/homepage/FeaturedProcesses";
import {motion, useScroll, useTransform} from "framer-motion";
import {LucideActivity, Sparkles, Zap} from "lucide-react";
import {useNavigate} from "react-router-dom";

const PlatformPage = () => {
    const navigate = useNavigate();
    const sectionRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    // Parallax scroll effects
    const {scrollYProgress} = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"]
    });

    const headerY = useTransform(scrollYProgress, [0, 0.2], [0, -30]);
    const headerOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);
    const featureScale = useTransform(scrollYProgress, [0.3, 0.4], [0.98, 1]);

    // Platform features
    const platformFeatures = [
        {
            icon: Sparkles,
            title: "Tailored AI Workflows",
            description: "We build custom compliance workflows designed specifically for your industry needs, eliminating overlapping requirements across multiple directives."
        },
        {
            icon: LucideActivity,
            title: "Human-Centered Approach",
            description: "Our AI acts as a compliance assistant, not a replacement. We automate repetitive tasks while keeping your team in control of strategic decisions."
        },
        {
            icon: Zap,
            title: "Enterprise Integration",
            description: "Seamlessly integrate with your existing systems with dedicated account management and industry-specific AI templates to ensure rapid adoption."
        }
    ];

    return (
        <div className="relative min-h-screen">
            <Helmet>
                <title>Verdea AI Platform | Custom Compliance Solutions</title>
                <meta name="description"
                      content="Verdea builds tailored AI-powered compliance solutions customized for your specific industry needs, eliminating overlapping requirements across multiple EU directives."/>
            </Helmet>

            {/* Background Elements - Apple style */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                {/* Primary gradient blur */}
                <div
                    className="absolute top-0 right-0 w-[50vw] h-[50vh] blur-3xl opacity-30"
                    style={{
                        background: 'radial-gradient(circle at center, rgba(37, 99, 235, 0.3) 0%, rgba(37, 99, 235, 0) 70%)',
                        transform: 'translate(20%, -20%)'
                    }}
                />

                {/* Secondary gradient blur */}
                <div
                    className="absolute bottom-0 left-0 w-[40vw] h-[40vh] blur-3xl opacity-20"
                    style={{
                        background: 'radial-gradient(circle at center, rgba(79, 70, 229, 0.3) 0%, rgba(79, 70, 229, 0) 70%)',
                        transform: 'translate(-20%, 20%)'
                    }}
                />

                {/* Subtle dot grid pattern */}
                <div
                    className="absolute inset-0 opacity-[0.015]"
                    style={{
                        backgroundImage: 'radial-gradient(rgba(37, 99, 235, 0.2) 1px, transparent 1px)',
                        backgroundSize: '40px 40px'
                    }}
                />
            </div>

            <NavBar/>

            <main className="relative pt-24 pb-16 min-h-screen overflow-hidden">
                <section
                    ref={sectionRef}
                    className="container mx-auto px-4 md:px-6 relative z-10"
                >
                    <div className="container mx-auto px-4 md:px-6 relative z-10">
                        <motion.div
                            ref={contentRef}
                            style={{y: headerY, opacity: headerOpacity}}
                            className="max-w-5xl mx-auto"
                        >
                            <div className="text-center mb-10 md:mb-16">
                                <motion.div
                                    initial={{opacity: 0, y: 20}}
                                    animate={{opacity: 1, y: 0}}
                                    transition={{duration: 0.6}}
                                >
                <span
                    className="inline-block px-4 py-1.5 mb-6 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-medium dark:bg-blue-900/20 dark:border-blue-800/30 dark:text-blue-400">
                  Custom Compliance Solutions
                </span>

                                    <h1 className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight">
                                        Turning Regulatory Burden into
                                        <br/>
                                        <span
                                            className="bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent">
                    Competitive Advantage
                  </span>
                                    </h1>

                                    <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed">
                                        Verdea builds tailored AI-powered compliance solutions customized for your specific industry needs and regulatory requirements. Our platform transforms complex EU directives into streamlined processes, helping your compliance team reduce costs by 65%, slash error rates by 86%, and regain 240+ hours annually.
                                    </p>
                                </motion.div>

                                {/* Feature cards */}
                                <motion.div
                                    initial={{opacity: 0, y: 30}}
                                    animate={{opacity: 1, y: 0}}
                                    transition={{duration: 0.6, delay: 0.2}}
                                    className="grid md:grid-cols-3 gap-6 md:gap-8 mb-12"
                                >
                                    {platformFeatures.map((feature, index) => {
                                        const Icon = feature.icon;
                                        return (
                                            <div
                                                key={index}
                                                className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 transition-all duration-300 hover:shadow-md"
                                            >
                                                <div className="flex flex-col items-center text-center">
                                                    <div
                                                        className="h-12 w-12 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 mb-4">
                                                        <Icon className="h-6 w-6"/>
                                                    </div>
                                                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{feature.title}</h3>
                                                    <p className="text-gray-500 dark:text-gray-400 text-sm">{feature.description}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </motion.div>


                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Interactive Calculator Section */}
                <motion.section
                    style={{scale: featureScale}}
                    className="py-16 md:py-20 bg-gray-50 dark:bg-gray-900/30"
                >
                    <div className="container mx-auto px-4 md:px-6">
                        <div className="max-w-4xl mx-auto text-center mb-12">
                            <motion.div
                                initial={{opacity: 0, y: 20}}
                                whileInView={{opacity: 1, y: 0}}
                                transition={{duration: 0.5}}
                                viewport={{once: true}}
                            >
                                <h2 className="text-3xl md:text-4xl font-medium text-gray-900 dark:text-gray-100 mb-4 leading-tight">
                                    Customizable <span
                                    className="text-blue-600 dark:text-blue-400">Compliance Modules</span>
                                </h2>
                                <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
                                    European companies are navigating a growing maze of regulations with overlapping requirements. 
                                    Our modular platform allows you to select from 18 AI-powered compliance components that can be 
                                    customized to your specific needs, eliminating duplicate work across directives while calculating your potential savings.
                                </p>
                            </motion.div>
                        </div>

                        <HeroSection/>
                    </div>
                </motion.section>

                {/* Featured Processes Section */}
                <section className="py-16 md:py-20">
                    <div className="container mx-auto px-4 md:px-6">
                        <div className="max-w-4xl mx-auto text-center mb-12">
                            <motion.div
                                initial={{opacity: 0, y: 20}}
                                whileInView={{opacity: 1, y: 0}}
                                transition={{duration: 0.5}}
                                viewport={{once: true}}
                            >
                                <h2 className="text-3xl md:text-4xl font-medium text-gray-900 dark:text-gray-100 mb-4 leading-tight">
                                    Customizable <span
                                    className="text-blue-600 dark:text-blue-400">Compliance Processes</span>
                                </h2>
                                <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
                                    At Verdea, we believe compliance should serve a genuine purpose beyond ticking boxes. Our platform 
                                    helps transform complex regulatory requirements into strategic opportunities. We don't fully automate 
                                    compliance—human oversight is crucial—but we make these processes significantly more efficient through 
                                    customized AI workflows for your industry.
                                </p>
                            </motion.div>
                        </div>

                        <FeaturedProcesses/>
                    </div>
                </section>

                {/* Footer */}
                <footer>
                    <Footer/>
                </footer>

            </main>
        </div>
    );
};

export default PlatformPage;
